import { Platform } from 'react-native';

// Native uses @react-native-firebase (lazy-required so a missing
// google-services file in Expo Go doesn't crash the import chain). Web uses
// the Firebase JS SDK via firebaseWeb.ts and is gated on user consent
// (GDPR/ePrivacy): the SDK is never initialised until `enableAnalytics()` is
// called, which the consent banner does only on Accept. Pre-init calls are
// queued so the order is preserved once init resolves.

type AnalyticsModule = {
  logEvent: (name: string, params?: Record<string, unknown>) => Promise<void>;
  setUserProperty: (name: string, value: string | null) => Promise<void>;
  setUserId: (id: string | null) => Promise<void>;
};

type CrashlyticsModule = {
  recordError: (error: Error, jsErrorName?: string) => void;
  log: (message: string) => void;
  setAttribute: (name: string, value: string) => void;
  setUserId: (id: string) => void;
};

type WebHandle = import('./firebaseWeb').WebAnalyticsHandle;

let analytics: AnalyticsModule | null = null;
let crashlytics: CrashlyticsModule | null = null;
let webAnalytics: WebHandle | null = null;
let nativeEnabled = false;
let queue: Array<() => void> = [];
let ready = false;

const flush = () => {
  ready = true;
  const drain = queue;
  queue = [];
  drain.forEach((fn) => {
    try {
      fn();
    } catch {
      /* swallow — analytics must never crash the app */
    }
  });
};

const enqueue = (fn: () => void) => {
  if (ready) {
    try {
      fn();
    } catch {
      /* swallow */
    }
  } else {
    queue.push(fn);
  }
};

const runWebInit = (): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { initFirebaseWebAnalytics } = require('./firebaseWeb') as typeof import('./firebaseWeb');
  return initFirebaseWebAnalytics()
    .then((handle) => {
      webAnalytics = handle;
    })
    .catch(() => {
      /* swallow — wrapper already logs in __DEV__ */
    });
};

if (Platform.OS === 'web') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { getConsent } = require('./consent') as typeof import('./consent');
  getConsent()
    .then(async (state) => {
      if (state === 'accepted') {
        await runWebInit();
      }
      // 'unknown' / 'rejected' — flush queue without webAnalytics so events
      // drop into a no-op. If the user later clicks Accept, enableAnalytics()
      // initialises the SDK and subsequent calls go through.
      flush();
    })
    .catch(() => flush());
} else {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const a = require('@react-native-firebase/analytics').default;
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const c = require('@react-native-firebase/crashlytics').default;
    analytics = a();
    crashlytics = c();
    nativeEnabled = true;
  } catch (err) {
    // Firebase native module unavailable (e.g. running in Expo Go without the
    // dev client). Stay silent in production; surface in __DEV__.
    if (__DEV__) {
      console.warn('[analytics] Firebase native module unavailable:', err);
    }
  }
  ready = true;
}

const sanitize = (
  params: Record<string, unknown>
): Record<string, string | number> | undefined =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, typeof v === 'boolean' ? String(v) : (v as string | number)])
  );

export type EventName =
  | 'app_open'
  | 'onboarding_complete'
  | 'job_view'
  | 'job_apply'
  | 'favorite_toggle'
  | 'applied_toggle'
  | 'theme_toggle'
  | 'language_change'
  | 'settings_open'
  | 'filter_open'
  | 'search_open'
  | 'fit_sort_toggle'
  | 'apply_kit_copy_field'
  | 'apply_browser_open'
  | 'apply_open_external'
  | 'application_status_change'
  | 'application_note_add'
  | 'application_remove'
  | 'application_add_manual'
  | 'alert_create'
  | 'alert_delete'
  | 'alert_toggle'
  | 'sign_in'
  | 'sign_up'
  | 'sign_out'
  | 'delete_account'
  | 'password_reset'
  | 'auth_error'
  | 'ad_impression'
  | 'cliked_on_ad'
  | 'ad_load_failed'
  | 'dev_hide_ads_toggle'
  | 'resume_create'
  | 'resume_edit'
  | 'resume_delete'
  | 'resume_duplicate'
  | 'resume_template_change'
  | 'resume_export'
  | 'resume_preview'
  | 'interstitial_shown'
  | 'interstitial_failed'
  | 'remove_ads_purchase_initiated'
  | 'remove_ads_purchase_completed'
  | 'remove_ads_purchase_cancelled'
  | 'remove_ads_purchase_failed'
  | 'remove_ads_restore_initiated'
  | 'remove_ads_restore_completed'
  | 'remove_ads_restore_empty'
  | 'remove_ads_restore_failed'
  | 'tour_view_step'
  | 'tour_skip'
  | 'tour_replay'
  | 'ota_update_available'
  | 'ota_update_applied';

export const track = (name: EventName, params?: Record<string, unknown>) => {
  if (__DEV__) {
    console.log('[analytics]', name, params ?? {});
  }
  const safeParams = params ? sanitize(params) : undefined;
  enqueue(() => {
    if (Platform.OS === 'web') {
      webAnalytics?.logEvent(name, safeParams);
    } else if (nativeEnabled && analytics) {
      analytics.logEvent(name, safeParams).catch(() => {});
    }
  });
};

export const setUserProperty = (name: string, value: string | null) => {
  enqueue(() => {
    if (Platform.OS === 'web') {
      webAnalytics?.setUserProperty(name, value);
    } else if (nativeEnabled && analytics) {
      analytics.setUserProperty(name, value).catch(() => {});
    }
  });
};

export const setUserId = (id: string | null) => {
  enqueue(() => {
    if (Platform.OS === 'web') {
      webAnalytics?.setUserId(id);
    } else if (nativeEnabled) {
      analytics?.setUserId(id).catch(() => {});
      if (id) crashlytics?.setUserId(id);
    }
  });
};

export const recordError = (error: Error, context?: string) => {
  if (__DEV__) {
    console.error('[crash]', context ?? '', error);
  }
  // Crashlytics has no web equivalent; native-only.
  if (!nativeEnabled || !crashlytics) return;
  if (context) crashlytics.log(context);
  crashlytics.recordError(error);
};

export const breadcrumb = (message: string) => {
  if (!nativeEnabled || !crashlytics) return;
  crashlytics.log(message);
};

// Called by the web consent banner on Accept. Idempotent.
export const enableAnalytics = async (): Promise<void> => {
  if (Platform.OS !== 'web' || webAnalytics) return;
  await runWebInit();
};

// Called by the web consent banner on Reject. The SDK was never loaded, so
// this is mostly defensive — clears the handle if init was already in
// flight.
export const disableAnalytics = (): void => {
  if (Platform.OS !== 'web') return;
  webAnalytics = null;
};

export default {
  track,
  setUserId,
  setUserProperty,
  recordError,
  breadcrumb,
  enableAnalytics,
  disableAnalytics,
};
